import React, { useEffect } from 'react';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import SEO from '@/components/SEO';

const PrivacyPolicyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO 
        title="Privacy Policy | Fearless IT"
        description="Learn how Fearless IT collects, uses, and protects your personal information."
      />
      <div className="min-h-screen bg-white">
        <MarketingHeader />
        
        {/* Hero Section */}
        <section className="pt-12 pb-6 sm:pt-16 sm:pb-8 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-carbon-black mb-4">
              Privacy Policy
            </h1>
            <p className="text-slate-gray">Last updated: January 2025</p>
          </div>
        </section>

        {/* Content */}
        <section className="py-12 sm:py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose prose-lg max-w-none">
              
              <h2 className="text-2xl font-bold text-carbon-black mt-8 mb-4">1. Introduction</h2>
              <p className="text-slate-gray mb-4">
                Fearless IT ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
              </p>

              <h2 className="text-2xl font-bold text-carbon-black mt-8 mb-4">2. Information We Collect</h2>
              <h3 className="text-xl font-semibold text-carbon-black mt-6 mb-3">Personal Information</h3>
              <p className="text-slate-gray mb-4">
                We may collect personal information that you voluntarily provide to us when you:
              </p>
              <ul className="list-disc pl-6 text-slate-gray mb-4 space-y-2">
                <li>Register for an account</li>
                <li>Request a quote or consultation</li>
                <li>Subscribe to our newsletter</li>
                <li>Contact us through our website</li>
                <li>Use our services</li>
              </ul>
              <p className="text-slate-gray mb-4">
                This information may include your name, email address, phone number, company name, and any other information you choose to provide.
              </p>

              <h3 className="text-xl font-semibold text-carbon-black mt-6 mb-3">Automatically Collected Information</h3>
              <p className="text-slate-gray mb-4">
                When you visit our website, we may automatically collect certain information about your device, including:
              </p>
              <ul className="list-disc pl-6 text-slate-gray mb-4 space-y-2">
                <li>IP address</li>
                <li>Browser type and version</li>
                <li>Operating system</li>
                <li>Pages visited and time spent</li>
                <li>Referring website addresses</li>
              </ul>

              <h2 className="text-2xl font-bold text-carbon-black mt-8 mb-4">3. How We Use Your Information</h2>
              <p className="text-slate-gray mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc pl-6 text-slate-gray mb-4 space-y-2">
                <li>Provide, operate, and maintain our services</li>
                <li>Process transactions and send related information</li>
                <li>Send you technical notices, updates, and support messages</li>
                <li>Respond to your comments, questions, and requests</li>
                <li>Communicate with you about products, services, and events</li>
                <li>Monitor and analyze trends, usage, and activities</li>
                <li>Detect, investigate, and prevent fraudulent transactions</li>
                <li>Personalize and improve your experience</li>
              </ul>

              <h2 className="text-2xl font-bold text-carbon-black mt-8 mb-4">4. Information Sharing</h2>
              <p className="text-slate-gray mb-4">
                We may share your information in the following situations:
              </p>
              <ul className="list-disc pl-6 text-slate-gray mb-4 space-y-2">
                <li><strong>Service Providers:</strong> With third-party vendors who perform services on our behalf</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                <li><strong>With Your Consent:</strong> When you have given us permission to share your information</li>
              </ul>

              <h2 className="text-2xl font-bold text-carbon-black mt-8 mb-4">5. Data Security</h2>
              <p className="text-slate-gray mb-4">
                We implement appropriate technical and organizational security measures to protect your personal information. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
              </p>

              <h2 className="text-2xl font-bold text-carbon-black mt-8 mb-4">6. Your Rights</h2>
              <p className="text-slate-gray mb-4">
                Depending on your location, you may have the following rights regarding your personal information:
              </p>
              <ul className="list-disc pl-6 text-slate-gray mb-4 space-y-2">
                <li>Access and receive a copy of your data</li>
                <li>Rectify or update your information</li>
                <li>Request deletion of your data</li>
                <li>Object to or restrict processing</li>
                <li>Data portability</li>
                <li>Withdraw consent at any time</li>
              </ul>

              <h2 className="text-2xl font-bold text-carbon-black mt-8 mb-4">7. Cookies and Tracking</h2>
              <p className="text-slate-gray mb-4">
                We use cookies and similar tracking technologies to collect and track information about your browsing activities. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
              </p>

              <h2 className="text-2xl font-bold text-carbon-black mt-8 mb-4">8. Third-Party Links</h2>
              <p className="text-slate-gray mb-4">
                Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to read the privacy policy of every website you visit.
              </p>

              <h2 className="text-2xl font-bold text-carbon-black mt-8 mb-4">9. Children's Privacy</h2>
              <p className="text-slate-gray mb-4">
                Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected personal information from a child, we will take steps to delete that information.
              </p>

              <h2 className="text-2xl font-bold text-carbon-black mt-8 mb-4">10. Changes to This Policy</h2>
              <p className="text-slate-gray mb-4">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
              </p>

              <h2 className="text-2xl font-bold text-carbon-black mt-8 mb-4">11. Contact Us</h2>
              <p className="text-slate-gray mb-4">
                If you have any questions about this Privacy Policy, please contact us at:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-carbon-black font-semibold">Fearless IT</p>
                <p className="text-slate-gray">Email: privacy@fearlessit.io</p>
                <p className="text-slate-gray">Website: https://fearlessit.io</p>
              </div>

            </div>
          </div>
        </section>

        <MarketingFooter />
      </div>
    </>
  );
};

export default PrivacyPolicyPage;
